import {customElement, property, state} from 'lit/decorators.js';
import {css, html, LitElement, PropertyValues, TemplateResult} from 'lit';
import {translate} from '@stefanholzapfel/lit-translate';
import {styleMap} from 'lit-html/directives/style-map.js';
import {repeat} from 'lit-html/directives/repeat.js';
import {classMap} from 'lit-html/directives/class-map.js';
import {SpHierarchicalSelectDialogButtonLabels, SpHierarchicalSelectOption} from './sp-hierarchical-select';
import {SpDefaultDialog} from '../../sp-dialog/sp-default-dialog';
import {unsafeCSSIcon} from "../../../styles/icons";
import {when} from 'lit-html/directives/when.js';

@customElement('sp-hierarchical-select-dialog')
export class SpHierarchicalSelectDialog extends LitElement {
    @property({type: Object})
    dialog: Promise<SpDefaultDialog>;
    @property({type: Array})
    options: SpHierarchicalSelectOption[];
    @property()
    value: (string | number) | (string | number)[];
    @property({type: Boolean})
    required = false;
    @property({type: Boolean, reflect: true})
    disabled = false;
    @property({type: Function})
    changeApproval?: () => Promise<'continue' | 'cancel'>;
    @property({type: Boolean})
    multi;
    @property({type: Function})
    emitChange: (value: (string | number) | (string | number)[]) => void;
    @property({type: Function})
    getLabel: (value: string | number, meta?: 'metaLabel' | 'infoSource' | 'infoImage', hasNoValue?: boolean) => string | TemplateResult;
    @property({type: Map})
    dialogButtonLabels: SpHierarchicalSelectDialogButtonLabels = {
        ok: 'OK',
        cancel: 'CANCEL',
        back: 'BACK'
    };
    @state()
    infoShow: boolean;
    @state()
    infoId: number | string;
    @state()
    infoSrc: string;
    @state()
    infoLoading = false;
    @state()
    tmpValue;
    @state()
    tmpParent = {id: 0} as SpHierarchicalSelectOption;

    static styles = [
        //language=CSS
        css`
            .select-list {
                list-style-type: none;
                overflow-y: scroll;
                position: relative;
                margin: 0;
                padding: 0;
            }

            .select-list::-webkit-scrollbar {
                display: none;
            }

            .select-list li {
                position: relative;
                display: block;
                width: 100%;
                height: 50px;
                margin-bottom: 1px;
                box-sizing: border-box;
                background-color: #fff;
                cursor: pointer;
            }

            .select-list li:last-child {
                margin: 0;
            }

            .select-list li.bg-color-b {
                background-color: rgb(var(--sp-color-b));
                color: #fff;
            }

            .select-list.border-left li {
                border-left: 10px solid #ccc;
            }

            .select-list li .option-label {
                display: block;
                width: 100%;
                height: 100%;
                font-size: 15px;
                line-height: 50px;
                box-sizing: border-box;
                padding: 0 10px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .select-list li .option-label.small {
                height: 50%;
                line-height: 20px;
                padding: 5px 10px 0 10px;
            }

            .select-list li .option-label.short {
                padding-right: 55px;
            }

            .select-list.multi li .option-label,
            .select-list.multi li .option-label.small {
                padding-left: 50px;
            }

            .select-list li .option-meta-label {
                display: block;
                width: 100%;
                height: 50%;
                font-size: 13px;
                line-height: 20px;
                box-sizing: border-box;
                padding: 0 10px 5px 10px;
                white-space: nowrap;
                overflow: hidden;
                box-sizing: border-box;
                color: #888;
            }

            .select-list.multi li .option-meta-label {
                padding-left: 50px;
            }

            .select-list li .option-meta-label.short {
                padding-right: 55px;
            }

            .select-list li.bg-color-b .option-meta-label {
                color: #fff;
            }

            .select-list li .option-info {
                display: block;
                position: absolute;
                top: 5px;
                right: 5px;
                width: 40px;
                height: 40px;
                background-size: auto 100%;
                background-repeat: no-repeat;
                background-position: center center;
            }

            .select-list li .option-info-icon {
                display: block;
                position: absolute;
                bottom: 5px;
                right: 5px;
                width: 16px;
                height: 16px;
                border-radius: 16px;
                background-size: auto 100%;
                background-repeat: no-repeat;
                background-position: center center;
                background-color: #fff;
                background-image: url(${unsafeCSSIcon('info-circle', {color: 'grey-dark', size: 'x128'})});
            }

            .select-list li .option-info.unlinked,
            .select-list li .option-info-icon.unlinked {
                cursor: default;
            }

            .select-list li .option-selected {
                display: block;
                position: absolute;
                top: 0;
                left: 0;
                width: 16px;
                height: 16px;
                border: 7px solid #ECECEC;
                border-radius: 3px;
                margin: 10px;
                background-color: #ECECEC;
            }

            .select-list li .option-selected + .option-label {
                padding-left: 50px;
            }

            .select-parent {
                font-size: 15px;
                line-height: 50px;
                box-sizing: border-box;
                padding: 0 10px 0 40px;
                margin-bottom: calc(var(--sp-default-padding) / 3);
                color: #fff;
                background-image: url(${unsafeCSSIcon('angle-left-thin', {color: 'white', size: 'x128'})});
                background-repeat: no-repeat;
                background-position: 12px center;
                background-size: 16px auto;
                cursor: pointer;
            }

            .container {
                position: relative;
                height: calc(100% - 55px);
            }

            .iframe {
                width: 100%;
                height: 100%;
            }

            iframe {
                width: 100%;
                height: 100%;
            }`
    ];

    render() {
        return html`
            ${when(!this.infoShow && this.tmpParent.id !== 0,
                    () => html`
                        <div class="select-parent"
                             style=${styleMap({
                                 'background-color': (!this.multi && this.tmpParent.id === this.tmpValue) ? 'rgb(var(--sp-color-b))' : 'rgb(var(--sp-color-d))'
                             })}
                             @click=${() => {
                                 this.selectParent(this.tmpParent)
                             }}>
                            ${this.getLabel(this.tmpParent.id)}
                        </div>
                    `)}
            ${when(!this.infoShow,
                    () => html`
                        <div class="container">
                            <ul class=${classMap({
                                'select-list': true,
                                'border-left': !this.multi
                            })}>
                                ${repeat(this.options
                                        .filter(option => option.parentId === this.tmpParent.id), option => option.id, option => {
                                    return html`
                                        <li
                                                class=${classMap({
                                                    'bg-color-b': !this.multi && option.id === this.tmpValue
                                                })}
                                                style=${styleMap({
                                                    'border-color': option.id === this.tmpValue ? 'rgb(var(--sp-color-b))' : 'rgb(var(--sp-color-d))'
                                                })}>
                                            ${when(this.multi, () => html`
                                                <span class="option-selected"
                                                      style=${styleMap({
                                                          ...this.tmpValue?.includes(option.id) && {'background-color': 'rgb(var(--sp-color-b))'},
                                                      })} @click=${() => {
                                                    this.selectOption(option)
                                                }}>                                                             
                                                                </span>`)}
                                            <span class=${classMap({
                                                "option-label": true,
                                                small: option.hasMetaLabel
                                            })}
                                                  @click=${() => {
                                                      this.selectOption(option)
                                                  }}>
                                                            ${ this.getLabel(option.id) }
                                                    </span>
                                            ${when(option.hasMetaLabel, () => html`
                                                <span class="option-meta-label"
                                                      @click=${() => {
                                                          this.selectOption(option)
                                                      }}>
                                                        ${this.getLabel(option.id, 'metaLabel')}</span>`)}
                                            ${when(option.hasInfo, () => html`
                                                <span class="option-info"
                                                      @click=${() => {
                                                          this.loadInfo(option)
                                                      }}
                                                      style=${styleMap({'background-image': `url(${this.getLabel(option.id, 'infoImage')})`})}>
                                                                </span>`)}
                                        </li>`
                                })}
                            </ul>
                        </div>`,
                    () => html`
                        <div class="optinfo iframe">
                            ${
                                    when(this.infoLoading, () => html`<span
                                            class="loading">${translate('LOADING')}</span>`)
                            }
                            <iframe id="opt-info" src=${this.infoSrc} @load=${this.loadInfoFinished}></iframe>
                        </div>`)
            }`;
    }

    async selectOption(option: SpHierarchicalSelectOption) {
        if (this.changeApproval) {
            const result = await this.changeApproval();
            if (result === 'continue') this.selectOptionApproved(option);
        } else {
            this.selectOptionApproved(option);
        }
    }

    selectOptionApproved(option: SpHierarchicalSelectOption) {
        if (this.isNode(option)) this.tmpParent = option;
        if (this.multi) {
            const index = this.tmpValue.indexOf(option.id);
            if (index > -1) {
                this.tmpValue.splice(index, 1);
            } else {
                this.tmpValue.push(option.id);
            }
            this.requestUpdate();
        } else {
            this.tmpValue = option.id;
            if (!this.isNode(option)) this.saveModal(option.id);
        }
    }

    selectParent(parent: any) {
        if (parent.parentId === 0) {
            if (!this.multi) {
                this.tmpValue = undefined;
            }
            this.tmpParent = {id: 0};
        } else {
            if (!this.multi) {
                this.tmpValue = parent.parentId;
            }
            this.options.forEach(option => {
                if (parent.parentId === option.id) this.tmpParent = option;
            });
        }
    }

    isNode(option: SpHierarchicalSelectOption): boolean {
        let isNode = false;
        this.options.forEach(_option => {
            if (!isNode && _option.parentId == option.id) {
                isNode = true;
            }
        });
        return isNode;
    };

    async loadInfo(option: SpHierarchicalSelectOption) {
        this.infoLoading = true;
        this.infoId = option.id;
        this.infoSrc = this.getLabel(option.id, 'infoSource') as string;
        this.infoShow = true;
    }

    loadInfoFinished() {
        this.infoLoading = false;
    }

    async saveModal(value: number | string) {
        if (this.infoShow) {
            this.emitChange(this.infoId);
            this.resetInfo();
        } else {
            this.emitChange(structuredClone(value));
        }
        (await this.dialog).hide();
    }

    async cancelModal() {
        this.resetInfo();
        (await this.dialog).hide(true);
    }

    async resetInfo() {
        this.infoShow = false;
        this.infoSrc = '';
        this.infoLoading = false;
        this.infoId = null;
    }

    checkSelectedOptions() {
        this.tmpValue = this.multi ? [] : undefined;
        this.tmpParent = {id: 0};
        if (this.value) this.tmpValue = structuredClone(this.value);
        this.options.forEach(option => {
            if (this.tmpValue === option.id) {
                if (this.isNode(option)) {
                    this.tmpParent = option;
                } else {
                    this.options.forEach(_option => {
                        if (option.parentId === _option.id) this.tmpParent = _option;
                    });
                }
            }
        });
    }

    firstUpdated(_changedProperties: PropertyValues) {
        super.firstUpdated(_changedProperties);
        if (!this.disabled) {
            this.checkSelectedOptions();
        }
    }

    async willUpdate(props: PropertyValues) {
        super.willUpdate(props);
        // Set correct buttons on relevant change
        if (props.has('options') || props.has('infoShow') || props.has('tmpParent') || props.has('multi')) {
            if (this.infoShow) {
                (await this.dialog).buttons = [
                    {
                        text: this.dialogButtonLabels.ok,
                        textPosition: 'center',
                        clickCallback: (dialogContent: HTMLCollection) => {
                            this.saveModal(this.tmpValue);
                        }
                    },
                    {
                        text: this.dialogButtonLabels.back,
                        textPosition: 'center',
                        clickCallback: (dialogContent: HTMLCollection) => {
                            this.resetInfo();
                        }
                    }
                ]
            } else if (this.multi || (!this.multi && !(this.tmpParent.id === 0))) {
                (await this.dialog).buttons = [
                    {
                        text: this.dialogButtonLabels.ok,
                        textPosition: 'center',
                        clickCallback: (dialogContent: HTMLCollection) => {
                            this.saveModal(this.tmpValue);
                        }
                    },
                    {
                        text: this.dialogButtonLabels.cancel,
                        textPosition: 'center',
                        clickCallback: (dialogContent: HTMLCollection) => {
                            this.cancelModal();
                        }
                    }
                ]
            } else {
                (await this.dialog).buttons = [
                    {
                        text: this.dialogButtonLabels.cancel,
                        textPosition: 'center',
                        clickCallback: (dialogContent: HTMLCollection) => {
                            this.cancelModal();
                        }
                    }
                ]
            }
        }
        // Check if value changed
        if (props.has('value')) {
            const prevValue = props.get('value');
            // Check if it had a value before
            if ((Array.isArray(prevValue) && (prevValue as []).length > 0) || (!Array.isArray(prevValue) && prevValue !== null && prevValue !== undefined && prevValue !== '')) {
                // Check it has no value now
                if ((Array.isArray(this.value) && this.value.length === 0) || (!Array.isArray(this.value) && (this.value === null || this.value === undefined || this.value === ''))) {
                    // Reset component
                    this.resetInfo();
                    this.checkSelectedOptions();
                }
            }
        }
    }

}
